<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/Research/AcademicIndirectCost/PayPeriods">Research</b-breadcrumb-item>
            <b-breadcrumb-item to="/Research/AcademicIndirectCost/PayPeriods">Academinc Indirect Cost</b-breadcrumb-item>
            <b-breadcrumb-item active>Pay Periods</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="bg-dark card-header text-white">Pay Periods</div>
            <div class="card-body">
                <fieldset class="border p-3 mb-2" style="height: 100%;">
                    <legend class="w-auto h5">Fiscal Year</legend>
                    <b-form-group>
                        <div class="row">
                            <div class="col-lg-6 col-md-12">
                                <select class="form-control" v-model="filterOptions.fiscalYearId" @change="onFilterSubmit()">
                                    <option :key="fiscalYear.id" :value="fiscalYear.id" v-for="fiscalYear in fiscalYears"><span>{{fiscalYear.year}}</option>
                                </select>
                            </div>
                        </div>                           
                    </b-form-group>
                </fieldset>
                <BTable
                        :busy="isLoading"
                        :fields="tableFields"
                        :items="payPeriods"
                        :responsive="true"
                        primary-key="id"
                        show-empty
                        striped
                        table-class="table-align-middle">

                    <template v-slot:cell(id)="row">
                        <div class="btn-group">
                            <router-link :to="{name:'PayPeriod',params:{ payPeriodId: row.item.id, fiscalYear: row.item.fiscalYear, payPeriodStartDate:row.item.fiscalPeriodStartDate, payPeriodStartDateDisplay:row.item.fiscalPeriodStartDateDisplay, canEdit: row.item.canEdit }}"
                                         class="btn btn-sm btn-primary d-print-none"
                                         data-toggle="tooltip"
                                         title="Edit Academic Indirect Cost for Pay Period">
                                Select
                            </router-link>
                            <button v-if="hasApplicationRole(applicationRoles.Administrator)" @click="onEditPayPeriodOpen(row.item.id, row.item.deadlineDate, row.item.fiscalPeriodStartDate)" class="btn btn-warning btn-sm d-print-none" title="Edit Pay Period"
                                    type="button"><i class="fa fa-pencil-alt"></i>
                            </button>
                        </div>
                    </template>

                    <template v-slot:cell(isCompleted)="row">
                        <span>{{ row.item.isCompleted ? "Completed" : "Not Completed" }}</span>
                    </template>

                    <template v-slot:cell(isClosed)="row">
                        <span>{{ row.item.isClosed ? "Closed" : "Open" }}</span>
                    </template>

                    <template v-slot:cell(fiscalPeriodStartDate)="row">
                        <span>{{ row.item.fiscalPeriodStartDate | moment('MM/DD/YYYY') }}</span>
                    </template>

                    <template v-slot:cell(fiscalPeriodEndDate)="row">
                        <span>{{ row.item.fiscalPeriodEndDate | moment('MM/DD/YYYY') }}</span>
                    </template>

                    <template v-slot:cell(deadlineDate)="row">
                        <span>{{ row.item.deadlineDate | moment('MM/DD/YYYY') }}</span>
                    </template>

                    <!-- Footer Pagination -->
                    <template v-slot:custom-foot>
                        <b-tr>
                            <b-td :colspan="tableFields.length">
                                <AppTablePagination
                                        :page.sync="filterOptions.page"
                                        :pageSize.sync="filterOptions.pageSize"
                                        :totalCount.sync="totalPayPeriods"
                                        v-on:update:page="loadData()"
                                        v-on:update:pageSize="onFilterSubmit()"
                                ></AppTablePagination>
                            </b-td>
                        </b-tr>
                    </template>

                </BTable>
            </div>
        </div>

        <AppModal @close="onEditPayPeriodClose()" v-if="isEditPayPeriodModalVisible">
            <template v-slot:header>Edit Pay Period {{ updatePayPeriod | moment("MMMM YYYY") }}</template>
            <form @submit.prevent="onEditPayPeriodSubmit()">
                <AppNotification type="warning">
                    <span>Changing the deadline date will do the following:</span>
                    <br>
                    <ul>
                        <li>Set all certification checks to false</li>
                        <li>Clear person who originally certified the pay period, if any</li>
                        <li>Clear the certification date, if any</li>
                        <li>Mark the pay period as incomplete</li>
                    </ul>
                    <span>Only future dates are allowed</span>
                </AppNotification>
                <div class="form-group">
                    <label for="deadlineDate">New Deadline Date</label>
                    <b-input-group class="mb-3">
                    <b-form-input
                        v-model="updatePayPeriodDeadlineDate"
                        type="text"
                        placeholder="MM/DD/YYYY"
                        :disabled="true"
                    ></b-form-input>
                    <b-input-group-append>
                        <b-form-datepicker
                        v-model="rawPayPeriodDeadlineDate"
                        button-only
                        right
                        reset-button
                        locale="en-US"
                        aria-controls="deadlineDate"
                        @context="updateDeadlineDateFromButton()"
                        ></b-form-datepicker>
                    </b-input-group-append>
                    </b-input-group>
                </div>
            </form>
            <template v-slot:footer>
                <button :disabled="isSubmitting" @click.prevent="onEditPayPeriodSubmit()" class="btn btn-primary" type="submit">Save
                </button>
                <button :disabled="isSubmitting" @click="onEditPayPeriodClose()" class="btn btn-danger" data-dismiss="modal"
                        type="button">
                    Close
                </button>
            </template>
        </AppModal>
    </div>
</template>

<script>
    import AppTablePagination from "@/components/shared/AppTablePagination";
    import AcademicIndirectCostService from "../../services/AcademicIndirectCostService";
    import AppNotification from "@/components/shared/AppNotification.vue";
    import {ToastType} from "@/services/models/shared/ToastType";
    import { ApplicationRole } from '@/services/models/Role';
    import AppModal from "@/components/shared/AppModal";

    export default {
        components: {
            AppTablePagination,
            AppNotification,
            AppModal
        },
        data() {
            return {
                applicationRoles: null,
                payPeriods: [],
                totalPayPeriods: 0,
                isLoading: false,
                fiscalYears: [],
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    fiscalYearId: null
                },
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    },
                    {
                        key: 'period',
                        label: 'Pay Period'
                    },
                    {
                        key: 'fiscalPeriodStartDate',
                        label: 'Start Date'
                    },
                    {
                        key: 'fiscalPeriodEndDate',
                        label: 'End Date'
                    },
                    {
                        key: 'deadlineDate',
                        label: 'Processing Deadline'
                    },
                    {
                        key: 'isCompleted',
                        label: 'Processing Status'
                    },
                    {
                        key: 'isClosed',
                        label: 'Period Status'
                    }
                ],
                isEditPayPeriodModalVisible: false,
                updatePayPeriodId: null,
                updatePayPeriod: null,
                rawPayPeriodDeadlineDate: null,
                updatePayPeriodDeadlineDate: null
            }
        },
        methods: {
            loadFiscalYears() {
                const vm = this;

                AcademicIndirectCostService.getFiscalYears()
                    .then(response => {
                        vm.fiscalYears = response;
                        if (response.length > 0) vm.filterOptions.fiscalYearId = response[0].id;
                        else vm.redirectToError();
                        vm.loadData();
                    })
                    .catch(() => {
                        vm.redirectToError()
                    })
            },

            /**
             * @description Loads the data from the API
             */
            loadData() {
                const vm = this;
                vm.isLoading = true;

                AcademicIndirectCostService.getPayPeriods(vm.filterOptions)
                    .then(response => {
                        vm.payPeriods = response.data.map((obj) => { return {...obj, period: this.$moment(obj.fiscalPeriodStartDate).format("MMMM YYYY"), isClosed: new Date(obj.deadlineDate) < new Date() ? true : false, canEdit: new Date(obj.deadlineDate) > new Date() && !obj.isCompleted, fiscalPeriodStartDateDisplay: this.$moment(obj.fiscalPeriodStartDate).format("MM-YYYY") } });
                        vm.totalPayPeriods = response.totalCount;
                    })
                    .catch(() => {
                        vm.redirectToError()
                    })
                    .finally(() => {
                        vm.isLoading = false;
                    })
            },
            
            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            onEditPayPeriodOpen(payPeriodId, deadlineDate, fiscalPeriodStartDate) {
                this.updatePayPeriod = fiscalPeriodStartDate;
                this.rawPayPeriodDeadlineDate = deadlineDate;
                this.updatePayPeriodDeadlineDate = deadlineDate;
                this.updatePayPeriodId = payPeriodId;
                this.isEditPayPeriodModalVisible = true;
            },

            onEditPayPeriodClose() {
                this.updatePayPeriodDisplay = null;
                this.updatePayPeriodDeadlineDate = null;
                this.rawPayPeriodDeadlineDate = null;
                this.updatePayPeriodId = null;
                this.isEditPayPeriodModalVisible = false;
            },

            onEditPayPeriodSubmit() {
                this.isSubmitting = true;

                if (this.updatePayPeriodDeadlineDate < new Date()) return this.alert(ToastType.Danger, 'Deadline date must be in the future');
                
                const payPeriod = {
                    id: this.updatePayPeriodId,
                    certifiedByPersonId: null,
                    isCompleted: false,
                    certifiedDate: null,
                    certifiedData: false,
                    certifiedReports: false,
                    certifiedPartiesNotified: false,
                    deadlineDate: this.updatePayPeriodDeadlineDate
                };
                AcademicIndirectCostService.updatePayPeriod(payPeriod)
                .then(() => {
                    this.alert(ToastType.Success, 'Academic Indirect Cost Distribution Pay Period Opened');
                })
                .catch(() => {
                    this.alert(ToastType.Danger, 'Failed to open Academic Indirect Cost Distribution Pay Period');
                })
                .finally(() => {
                    this.onEditPayPeriodClose();
                    this.isSubmitting = false;
                });
            },
            
            updateDeadlineDateFromButton () {
                if (this.rawPayPeriodDeadlineDate !== undefined && this.rawPayPeriodDeadlineDate !== null && this.rawPayPeriodDeadlineDate !== '') {
                    this.updatePayPeriodDeadlineDate = this.$moment(this.rawPayPeriodDeadlineDate).format("MM/DD/YYYY");
                } else {
                    this.rawPayPeriodDeadlineDate = null;
                    this.updatePayPeriodDeadlineDate = null;
                }
            }
        },
        created() {
            this.applicationRoles = ApplicationRole;
            this.loadFiscalYears();
        }
    }
</script>

